<template>
    <card :to="url" :class="cardBg" :data-bs-theme="cardTheme" @mouseenter="onMouseEnter" @mouseleave="onMouseLeave">
        <template #default>
            <property-card-image :property="property" :cardHover="hover"/>
            <card-body class="position-relative">
                <div class="text-primary x-small text-uppercase spaced-letters mb-1 d-flex align-items-center" v-if="property.city && property.state">
                    <div>
                        {{ property.city }}, {{ property.state }} {{ (property.country && (property.country != 'US')) ? ', '+property.country : '' }}
                    </div>
                    <form-stars-rating v-if="property.cachedRatingsAvg" :value="Math.ceil(property.cachedRatingsAvg)" :readonly="true" class="mx-2 xx-small" />
                    <div v-if="property.cachedRatingsCount">
                        ({{ property.cachedRatingsCount }})
                    </div>
                </div>
                <card-title :class="singleLineName ? 'text-truncate' : null">{{ property.name }}</card-title>

                <div v-if="property.isHotel && property.canBookHotelRooms" class="text-primary">
                    <div v-if="property.status != 2 && displayQuotes">
                        <template v-if="hotelQuoteRate">
                            {{ hotelQuoteRate }}
                        </template>
                        <template v-else>
                            Inquire for rates
                        </template>
                    </div>
                    <div v-else-if="property.status == 2 || !property.hotelScheduleAvgRange">
                        Inquire for rates
                    </div>
                    <div v-else>
                        Avg. ${{ property.hotelScheduleAvgRange.replace('-', ' - $')+((property.hotelScheduleAvgRange.indexOf('-') != -1) ? ' ' : '') }}/night
                    </div>

                    <div class="d-flex align-items-start">
                        <small v-if="hotelBookableLabel" class="mt-2">{{ hotelBookableLabel }}</small>
                        <property-card-icons v-else :property="property" :useBedsCount="useBedsCount" class="x-small mt-2"/>
                    </div>
                </div>

                <container v-else>
                    <row>
                        <column :col="12" class="text-primary">
                            <template v-if="property.status != 2 && displayQuotes">
                                <template v-if="quoteRate">
                                    {{ quoteRate }}
                                </template>
                                <template v-else>
                                    Inquire for rates
                                </template>
                            </template>
                            <template v-else-if="property.status == 2 || !property.schedulePriceAvg">
                                Inquire for rates
                            </template>
                            <template v-else>
                                Avg. {{ money(property.schedulePriceAvg) }}/night
                            </template>
                        </column>
                        <column :col="12">   
                            <property-card-icons :property="property" :useBedsCount="useBedsCount" :marginBetween="2" :limited="true" class="x-small text-primary mt-1 text-end"/>
                        </column>
                    </row>
                </container>
                <div v-if="quoteRate && property.traditional" class="small text-muted">
                    Includes the full cost of {{ traditionalMonthsList }}
                </div>

                <div v-if="property.serviceLevel == 'W'" class="position-absolute top-0 end-0 text-primary me-2 mt-1 p-1">
                    <div class="p-1 clickable hover-scale" @click.prevent.stop="showModal=true" style="width:32px; height:32px;">
                        <marquis-circle-icon />
                    </div>
                </div>
            </card-body>

            <marquis-modal v-if="showModal && (property.serviceLevel == 'W')" @close="showModal=false"/>

            <travelers-fav-properties-icon :property="property" :size="14" :showLabel="false" class="position-absolute top-0 end-0 clickable bg-body rounded-circle text-primary me-2 mt-2 d-flex align-items-center justify-content-center border-primary border hover-scale" style="width:28px; height:28px;"/>
            
        </template>
    </card>
</template>

<script type="text/javascript">

    import PropertyCardImage from '@/components/Content/Public/Properties/Cards/Parts/Image.vue';
    import PropertyCardIcons from '@/components/Content/Public/Properties/Cards/Parts/Icons.vue';
    import CommonComponents from '@/components/Common.vue';
    import MarquisModal from '@/components/Content/Public/Properties/MarquisModal.vue';
    import TravelersFavPropertiesIcon from '@/components/Content/Travelers/Properties/FavoriteIcon.vue';

    import MarquisCircleIcon from '@/assets/logos/marquis-circle-icon.svg.vue';

    import ApiObject from '@/classes/ApiObject.js';
    import DateObject from '@/classes/DateObject.js';
    import Formatter from '@/classes/Formatter.js';
    import Utilities from '@/classes/Utilities.js';

    import { UseUIStore } from '@/store/UI.js';

    export default {
        components: {
            MarquisCircleIcon,
            PropertyCardImage,
            PropertyCardIcons,
            MarquisModal,
            TravelersFavPropertiesIcon,
            ...CommonComponents,
        },

        setup() {
            return {
                uiStore: UseUIStore(),
            };
        },

        data() {
            return {
                hover: false,
                showModal: false,
            };
        },

        emits: ['onHover', 'offHover'],

        props: {
            property: {
                type: ApiObject,
                required: true,
            },

            checkIn: {
                type: DateObject,
            },

            checkOut: {
                type: DateObject,
            },

            adults: {
                type: Number,
            },

            children: {
                type: Number,
            },

            quotePerNight: {
                type: Boolean,
                default: false,
            },

            useBedsCount: {
                type: Boolean,
                default: false,
            },

            singleLineName: {
                type: Boolean,
                default: false,
            },

            disableNoRates: {
                type: Boolean,
                default: false,
            },
        },

        computed: {
            
            url() {
                let url = `/properties/${this.property.slug || this.property.id }`;
                let query = {};
                if ((this.checkIn) && (this.checkOut)) {
                    query.start = this.checkIn.format('Y-m-d');
                    query.end = this.checkOut.format('Y-m-d');
                }
                if ((this.adults) && (this.adults > 1)) {
                    query.adults = this.adults;
                }
                if (this.children) {
                    query.children = this.children;
                }
                if (Object.keys(query).length) {
                    url += '?'+new URLSearchParams(query).toString();
                }
                return url;
            },
            
            cardTheme() {
                
                if (this.property.serviceLevel == 'W') {
                    return 'casamarquis';
                }
                return null;
            },

            cardBg() {

                let cls = [];

                if (
                    (this.disableNoRates) &&
                    (this.displayQuotes)
                ) {
                    let noRates = false;
                    if ((this.property.isHotel) && (this.property.canBookHotelRooms)) {
                        if (!this.hotelQuoteRate) {
                            noRates = true;
                        }
                    }
                    else if (!this.quoteRate) {
                        noRates = true;
                    }
                    
                    if (noRates) {
                        cls.push('grayscale-50');
                        cls.push('opacity-70');
                    }

                }

                if (this.hover) {
                    cls.push('border-primary');

                    if ((this.property.serviceLevel == 'W') && (this.uiStore.theme != 'casamarquis')) {
                        cls.push('bg-light');
                    }
                }
                else if (this.property.serviceLevel == 'W') {
                    if (this.uiStore.theme == 'casamarquis') {
                        cls.push('bg-light');
                    }
                    
                }
                else {
                    cls.push('bg-light');
                }
                return cls;
            },

            displayQuotes() {
                if (
                    (this.checkIn) &&
                    (this.checkOut) &&
                    (this.checkIn.diffInDays(this.checkOut) > 0)
                ) {
                    return true;
                }
                return false;
            },
            
            numHotelRooms() {
                if (
                    (this.property) &&
                    (this.property.isHotel) &&
                    (this.property.hotelRoomsCount)
                ) {
                    return this.property.hotelRoomsCount;
                }
                return null;
            },

            hotelShortLabel() {

                if (
                    (this.property) &&
                    (this.property.hotelSettings) &&
                    (this.property.hotelSettings.hotelTypeShortLabel)
                ) {
                    return this.property.hotelSettings.hotelTypeShortLabel;
                }

                return 'Hotel';
            },

            hotelRoomsLabel() {
                if (this.numHotelRooms) {
                    let plural = 'Suites';
                    let singular = 'Suite';

                    if (
                        (this.property) &&
                        (this.property.hotelSettings) &&
                        (this.property.hotelSettings.roomsLabel)
                    ) {
                        singular = Utilities.ucFirst(this.property.hotelSettings.roomsLabel);
                        plural = singular+'s';
                    }

                    return Formatter.pluralize(this.numHotelRooms, plural, singular);
                }
                return null;
            },


            hotelBookableLabel() {
                if (
                    (this.numHotelRooms) &&
                    (this.property.canBookHotelRooms) &&
                    (this.property.hotelSettings)
                ) {
                    if (this.property.hotelAverageRate) {
                        if (
                            (this.property.hotelAverageRate.bookableRooms) &&
                            (this.property.hotelAverageRate.bookableRooms > 0)
                        ) {
                            let lbl = '';
                            if (this.property.hotelAverageRate.entireBookable) {
                                lbl += 'Entire '+this.hotelShortLabel+' or ';
                            }

                            if (this.property.hotelAverageRate.bookableRooms < this.numHotelRooms) {
                                lbl += this.property.hotelAverageRate.bookableRooms+'/';
                            }

                            lbl += this.numHotelRooms+' '+this.hotelRoomsLabel +' Available';

                            return lbl;
                        }
                        else if (this.property.hotelAverageRate.entireBookable) {
                            return 'Entire '+this.hotelShortLabel;
                        }
                    }
                    else {
                        let lbl = '';
                        if (this.property.canBookEntireHotel) {
                            lbl += 'Entire '+this.hotelShortLabel+' or ';
                        }
                        lbl += this.numHotelRooms+' '+this.hotelRoomsLabel +' Available';

                        return lbl;
                    }
                }
                return null;
            },

            quoteRate() {
                if (
                    (this.displayQuotes) &&
                    (this.property) &&
                    (this.property.displayRate)
                ) {
                    let perLbl = 'stay';
                    let amount = this.property.displayRate.total;

                    if (this.quotePerNight) {
                        amount = this.property.displayRate.average;
                        perLbl = 'night';
                    }
                    
                    let depositLbl = '';
                    if (this.property.displayRate.deposit) {
                        depositLbl = ' + deposit';
                    }

                    return this.money(amount)+'/'+perLbl+depositLbl;
                }
                return null;
            },

            hotelQuoteRate() {
                if (
                    (this.displayQuotes) &&
                    (this.property) &&
                    (this.property.hotelAverageRate)
                ) {
                    let min = this.property.hotelAverageRate.min;
                    let max =  this.property.hotelAverageRate.max;
                    if (!min) min = max;
                    if (!max) max = min;

                    if ((!min) && (!max)) {
                        return null;
                    }

                    let perLbl = 'stay';

                    if (this.quotePerNight) {
                        let numNights = this.checkIn.diffInDays(this.checkOut);
                        min = Math.ceil(min / numNights);
                        max = Math.ceil(max / numNights);
                        perLbl = 'night';
                    }

                    if (min != max) {
                        return this.money(min)+' - '+this.money(max)+' /'+perLbl;
                    }
                    else {
                        return this.money(min)+'/'+perLbl;
                    }
                }

                return null;
            },

            traditionalMonthsList() {
                if (
                    (this.property) &&
                    (this.property.traditional) &&
                    (this.checkIn) &&
                    (this.checkOut)
                ) {
                    let start = this.checkIn.copy();
                    let end = this.checkOut.copy();
                    start.startOfMonth();
                    end.endOfMonth();

                    let months = [];
                    while (start.time <= end.time) {
                        months.push(start.format('F'));
                        start.month += 1;
                    }

                    if (months.length == 2) {
                        return months.join(' and ');
                    }
                    
                    if (months.length > 1) {
                        months[months.length - 1] = 'and '+months[months.length - 1];
                    }

                    return months.join(', ');
                }

                return null;
            },
        },

        methods: {
            money(amount) {
                return Formatter.money(amount, 0);
            },

            onMouseEnter() {
                this.hover = true;
                this.$emit('onHover', this.property);
            },

            onMouseLeave() {
                this.hover = false;
                this.$emit('offHover', this.property);
            },
        }
    };

</script>
